/**
 * Admin Token Storage Service
 * 
 * Provides reliable token storage and persistence for admin authentication.
 * Fixes issues with token storage, retrieval, and expiration validation.
 */

export interface AdminTokenInfo {
  token: string;
  expiresAt: string;
  issuedAt: string;
  userId: string;
  user?: any;
}

export interface TokenValidationResult {
  isValid: boolean;
  isExpired: boolean;
  expiresIn?: number; // milliseconds until expiration
  reason?: string;
}

class AdminTokenStorage {
  private readonly ADMIN_TOKEN_KEY = 'adminToken';
  private readonly ADMIN_TOKEN_INFO_KEY = 'adminTokenInfo';

  /**
   * Store admin token with proper expiration handling
   */
  storeToken(token: string, expiresAt?: string, user?: any): void {
    try {
      console.log('AdminTokenStorage: Storing admin token');
      
      // Parse JWT to extract expiration if not provided
      let tokenExpiresAt = expiresAt;
      let issuedAt = new Date().toISOString();
      let userId = 'admin';

      if (!tokenExpiresAt) {
        const tokenInfo = this.parseJwtToken(token);
        if (tokenInfo) {
          tokenExpiresAt = tokenInfo.expiresAt;
          issuedAt = tokenInfo.issuedAt;
          userId = tokenInfo.userId;
        }
      }

      // Default to 1 hour if no expiration found
      if (!tokenExpiresAt) {
        tokenExpiresAt = new Date(Date.now() + 3600000).toISOString();
        console.warn('AdminTokenStorage: No expiration found, defaulting to 1 hour');
      }

      // Store token
      localStorage.setItem(this.ADMIN_TOKEN_KEY, token);

      // Store token info
      const tokenInfo: AdminTokenInfo = {
        token,
        expiresAt: tokenExpiresAt,
        issuedAt,
        userId,
        user
      };

      localStorage.setItem(this.ADMIN_TOKEN_INFO_KEY, JSON.stringify(tokenInfo));

      console.log('AdminTokenStorage: Token stored successfully', {
        expiresAt: tokenExpiresAt,
        userId,
        hasUser: !!user
      });

    } catch (error) {
      console.error('AdminTokenStorage: Failed to store token:', error);
      throw new Error('Failed to store admin token');
    }
  }

  /**
   * Retrieve admin token from storage.
   * Idle timeout is enforced by sessionExpirationPolicy, not by deleting the JWT here.
   */
  getToken(): string | null {
    try {
      const token = localStorage.getItem(this.ADMIN_TOKEN_KEY);
      if (!token) {
        return null;
      }
      return token;
    } catch (error) {
      console.error('AdminTokenStorage: Failed to retrieve token:', error);
      return null;
    }
  }

  /**
   * Get token info including expiration details
   */
  getTokenInfo(): AdminTokenInfo | null {
    try {
      const tokenInfoStr = localStorage.getItem(this.ADMIN_TOKEN_INFO_KEY);
      if (!tokenInfoStr) {
        return null;
      }

      const tokenInfo = JSON.parse(tokenInfoStr);
      return tokenInfo;
    } catch (error) {
      console.error('AdminTokenStorage: Failed to parse token info:', error);
      this.clearToken(); // Clear corrupted data
      return null;
    }
  }

  /**
   * Validate token without aggressive checks
   * Only validates on actual usage, not proactively
   */
  validateToken(): TokenValidationResult {
    try {
      const token = localStorage.getItem(this.ADMIN_TOKEN_KEY);
      if (!token) {
        return {
          isValid: false,
          isExpired: false,
          reason: 'No token found'
        };
      }

      const tokenInfo = this.getTokenInfo();
      if (!tokenInfo) {
        return {
          isValid: false,
          isExpired: false,
          reason: 'No token info found'
        };
      }

      // Check expiration
      const expirationTime = new Date(tokenInfo.expiresAt).getTime();
      const currentTime = Date.now();
      const isExpired = currentTime >= expirationTime;

      if (isExpired) {
        return {
          isValid: false,
          isExpired: true,
          reason: 'Token expired'
        };
      }

      const expiresIn = expirationTime - currentTime;
      return {
        isValid: true,
        isExpired: false,
        expiresIn
      };

    } catch (error) {
      console.error('AdminTokenStorage: Token validation error:', error);
      return {
        isValid: false,
        isExpired: false,
        reason: 'Validation error'
      };
    }
  }

  /**
   * Check if token is expiring soon (within 5 minutes)
   */
  isTokenExpiringSoon(): boolean {
    const validation = this.validateToken();
    if (!validation.isValid || !validation.expiresIn) {
      return false;
    }

    const fiveMinutesInMs = 5 * 60 * 1000;
    return validation.expiresIn < fiveMinutesInMs;
  }

  /**
   * Clear admin token from storage
   */
  clearToken(): void {
    try {
      localStorage.removeItem(this.ADMIN_TOKEN_KEY);
      localStorage.removeItem(this.ADMIN_TOKEN_INFO_KEY);
      console.log('AdminTokenStorage: Token cleared from storage');
    } catch (error) {
      console.error('AdminTokenStorage: Failed to clear token:', error);
    }
  }

  /**
   * Check if an admin token is stored.
   * Whether that session may continue is decided by sessionExpirationPolicy.
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem(this.ADMIN_TOKEN_KEY);
  }

  /**
   * Get current admin user info
   */
  getCurrentUser(): any | null {
    const tokenInfo = this.getTokenInfo();
    return tokenInfo?.user || null;
  }

  /**
   * Get admin user ID
   */
  getUserId(): string | null {
    const tokenInfo = this.getTokenInfo();
    return tokenInfo?.userId || null;
  }

  /**
   * Parse JWT token to extract information
   */
  private parseJwtToken(token: string): { expiresAt: string; issuedAt: string; userId: string } | null {
    try {
      if (!token || typeof token !== 'string') {
        return null;
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payload = JSON.parse(atob(parts[1] || ''));
      
      return {
        expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : new Date(Date.now() + 3600000).toISOString(),
        issuedAt: payload.iat ? new Date(payload.iat * 1000).toISOString() : new Date().toISOString(),
        userId: payload.sub || payload.userId || payload.email || 'admin'
      };
    } catch (error) {
      console.error('AdminTokenStorage: Failed to parse JWT token:', error);
      return null;
    }
  }

  /**
   * Update token expiration (useful for token refresh)
   */
  updateTokenExpiration(newExpiresAt: string): boolean {
    try {
      const tokenInfo = this.getTokenInfo();
      if (!tokenInfo) {
        return false;
      }

      tokenInfo.expiresAt = newExpiresAt;
      localStorage.setItem(this.ADMIN_TOKEN_INFO_KEY, JSON.stringify(tokenInfo));
      
      console.log('AdminTokenStorage: Token expiration updated:', newExpiresAt);
      return true;
    } catch (error) {
      console.error('AdminTokenStorage: Failed to update token expiration:', error);
      return false;
    }
  }

  /**
   * Get session duration in milliseconds
   */
  getSessionDuration(): number | null {
    try {
      const tokenInfo = this.getTokenInfo();
      if (!tokenInfo) {
        return null;
      }

      const issuedAt = new Date(tokenInfo.issuedAt).getTime();
      const currentTime = Date.now();
      return currentTime - issuedAt;
    } catch (error) {
      console.error('AdminTokenStorage: Failed to calculate session duration:', error);
      return null;
    }
  }
}

export const adminTokenStorage = new AdminTokenStorage();